import { useHandler } from "@redotech/react-util/hook";
import {
  AddressPropertyField,
  SegmentStringOperator,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import {
  rcoAddressPropertiesSegmentSchema,
  RcoAddressPropertiesSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { OperatorSelector } from "@redotech/redo-web-conversion/conversion/operator-selector";
import { SegmentConjunctionSelect } from "@redotech/redo-web-marketing/marketing/segments/segment-conjunction-select";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../..";

const INITIAL_VISIBLE_RULES = 10;
const SHOW_MORE_BATCH_SIZE = 25;

function conjunctionToReadonlyText(conjunction: ConjunctionMode): string {
  switch (conjunction) {
    case ConjunctionMode.AND:
      return t("All conditions must be true");
    case ConjunctionMode.OR:
      return t("Any condition must be true");
    default:
      assertNever(conjunction);
  }
}

function addressPropertyFieldToText(): Record<AddressPropertyField, string> {
  return {
    [AddressPropertyField.ADDRESS1]: t("Address 1"),
    [AddressPropertyField.ADDRESS2]: t("Address 2"),
    [AddressPropertyField.CITY]: t("City"),
    [AddressPropertyField.PROVINCE]: t("State / province"),
    [AddressPropertyField.POSTAL_CODE]: t("Postal code"),
    [AddressPropertyField.COUNTRY]: t("Country"),
    [AddressPropertyField.NAME]: t("Name"),
  };
}

function segmentStringOperatorToText(): Record<SegmentStringOperator, string> {
  return {
    [SegmentStringOperator.EQUALS]: "equals",
    [SegmentStringOperator.NOT_EQUALS]: t("does not equal"),
    [SegmentStringOperator.CONTAINS]: "contains",
    [SegmentStringOperator.IS_CONTAINED_IN]: t("is contained in"),
    [SegmentStringOperator.NOT_CONTAINS]: t("does not contain"),
    [SegmentStringOperator.STARTS_WITH]: t("starts with"),
  };
}

function formatRule(rule: {
  field: AddressPropertyField;
  operator: SegmentStringOperator;
  value: string;
}): string {
  return `${addressPropertyFieldToText()[rule.field]} ${segmentStringOperatorToText()[rule.operator]} "${rule.value}"`;
}

export const AddressPropertiesInspectorContent = memo(
  function AddressPropertiesInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const segment = useMemo(
      () => rcoAddressPropertiesSegmentSchema.parse(step.segment),
      [step],
    );

    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_RULES);

    const totalRules = segment.rules.length;
    const shownRules = segment.rules.slice(0, visibleCount);
    const remainingCount = Math.max(0, totalRules - visibleCount);

    const handleShowMore = useHandler(() => {
      setVisibleCount((prev) => prev + SHOW_MORE_BATCH_SIZE);
    });

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {conjunctionToReadonlyText(segment.conjunction)}
        </Text>
        {shownRules.map((rule, index) => (
          <Text
            fontSize="xs"
            fontWeight="medium"
            key={index}
            textColor="primary"
          >
            {formatRule(rule)}
          </Text>
        ))}
        {remainingCount > 0 && (
          <RedoButton
            hierarchy="secondary"
            IconTrailing={ChevronDownIcon}
            onClick={handleShowMore}
            size="xs"
            text={`Show ${Math.min(remainingCount, SHOW_MORE_BATCH_SIZE)} more (${remainingCount} remaining)`}
          />
        )}
      </Flex>
    );
  },
);

export const EditableAddressPropertiesInspectorContent = memo(
  function EditableAddressPropertiesInspectorContent({
    step,
    setStep,
  }: {
    step: PartialWithRequiredType<RcoSegmentStep>;
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoAddressPropertiesSegmentSchema.parse(step.segment),
      [step],
    );

    const updateRules = useHandler(
      (rules: RcoAddressPropertiesSegmentSchema["rules"]) => {
        const updatedSegment: RcoAddressPropertiesSegmentSchema = {
          ...segment,
          rules,
        };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleFieldChange = useHandler(
      (index: number, field: AddressPropertyField) => {
        updateRules(
          segment.rules.map((rule, i) =>
            i === index ? { ...rule, field } : rule,
          ),
        );
      },
    );

    const handleOperatorChange = useHandler(
      (index: number, operator: SegmentStringOperator) => {
        updateRules(
          segment.rules.map((rule, i) =>
            i === index ? { ...rule, operator } : rule,
          ),
        );
      },
    );

    const handleValueChange = useHandler((index: number, value: string) => {
      updateRules(
        segment.rules.map((rule, i) =>
          i === index ? { ...rule, value } : rule,
        ),
      );
    });

    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_RULES);

    const handleAddRule = useHandler(() => {
      const newLength = segment.rules.length + 1;
      if (visibleCount < newLength) {
        setVisibleCount(newLength);
      }
      updateRules([
        ...segment.rules,
        {
          field: AddressPropertyField.ADDRESS1,
          operator: SegmentStringOperator.CONTAINS,
          value: "",
        },
      ]);
    });

    const handleShowMore = useHandler(() => {
      setVisibleCount((prev) => prev + SHOW_MORE_BATCH_SIZE);
    });

    const handleRemoveRule = useHandler((index: number) => {
      if (segment.rules.length <= 1) {
        return;
      }
      updateRules(segment.rules.filter((_, i) => i !== index));
    });

    const handleConjunctionChange = useHandler(
      (conjunction: ConjunctionMode) => {
        const updatedSegment: RcoAddressPropertiesSegmentSchema = {
          ...segment,
          conjunction,
        };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const totalRules = segment.rules.length;
    const shownRules = segment.rules.slice(0, visibleCount);
    const remainingCount = Math.max(0, totalRules - visibleCount);

    return (
      <Flex align="flex-start" dir="column" gap="md" w="full">
        <SegmentConjunctionSelect
          mode={segment.conjunction}
          setMode={handleConjunctionChange}
          textAfterConjunction={t("of these conditions must be true")}
        />
        {shownRules.map((rule, index) => (
          <Flex
            bgColor="primary"
            borderColor="secondary"
            borderRadius="sm"
            borderStyle="solid"
            borderWidth="1px"
            dir="column"
            gap="sm"
            key={index}
            p="md"
            w="full"
          >
            <Flex align="center" dir="row" gap="sm" justify="space-between">
              <Flex align="center" dir="row" gap="sm" wrap="wrap">
                <OperatorSelector<AddressPropertyField>
                  availableOptions={Object.values(AddressPropertyField)}
                  onOperatorSelected={(field) =>
                    handleFieldChange(index, field)
                  }
                  operatorToText={addressPropertyFieldToText()}
                  selectedOperator={rule.field}
                />
                <OperatorSelector<SegmentStringOperator>
                  availableOptions={Object.values(SegmentStringOperator)}
                  onOperatorSelected={(operator) =>
                    handleOperatorChange(index, operator)
                  }
                  operatorToText={segmentStringOperatorToText()}
                  selectedOperator={rule.operator}
                />
              </Flex>
              <Flex shrink={0}>
                <RedoButton
                  aria-label={t("Remove")}
                  disabled={segment.rules.length <= 1}
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={() => handleRemoveRule(index)}
                  size="xs"
                  theme="destructive"
                />
              </Flex>
            </Flex>
            <RedoTextInput
              description={
                rule.field === AddressPropertyField.PROVINCE
                  ? t(
                      "Use the state/province code {{platformName}} sends, e.g. 'UT' for Utah, 'NSW' for New South Wales — not the full name.",
                      { platformName: "Shopify" },
                    )
                  : rule.field === AddressPropertyField.COUNTRY
                    ? t(
                        "Use the 2-letter ISO country code {{platformName}} sends, e.g. 'US' for United States — not the full name.",
                        { platformName: "Shopify" },
                      )
                    : undefined
              }
              fullWidth
              placeholder={t("Value")}
              setValue={(value) => handleValueChange(index, value)}
              size="sm"
              value={rule.value}
            />
          </Flex>
        ))}
        {remainingCount > 0 && (
          <RedoButton
            hierarchy="tertiary"
            IconTrailing={ChevronDownIcon}
            onClick={handleShowMore}
            size="sm"
            text={`Show ${Math.min(remainingCount, SHOW_MORE_BATCH_SIZE)} more (${remainingCount} remaining)`}
          />
        )}
        <RedoButton
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={handleAddRule}
          size="sm"
          text={t("Add condition")}
        />
      </Flex>
    );
  },
);
